import React, { useState } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    // Simple validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus("error");
      setErrorMessage("Venligst udfyld alle påkrævede felter.");
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus("error");
        setErrorMessage(errData.message || "Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    }
  };

  return (
    <div className="border border-primary-200 bg-white p-6 md:p-8 shadow-sm">

      {status === "success" ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600 mb-3" />
          <h4 className="font-display text-lg font-bold text-emerald-900 mb-1">
            Besked modtaget!
          </h4>
          <p className="text-emerald-700 text-sm">
            Tak for din henvendelse. Vi vender tilbage til dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus("idle")}
            className="mt-4 text-xs font-semibold text-emerald-800 hover:text-emerald-950 underline"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -z-10 overflow-hidden w-0 h-0" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-primary-700 mb-1.5">
              Navn <span className="text-brand-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              placeholder="Dit fulde navn"
              value={formData.name}
              onChange={handleChange}
              className="w-full rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 text-primary-900 placeholder-primary-400 focus:border-brand-500 focus:bg-white focus:ring-1 focus:ring-brand-500 focus:outline-none transition-all duration-200 text-sm"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-primary-700 mb-1.5">
                Telefonnummer <span className="text-brand-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="Tlf. nummer"
                value={formData.phone}
                onChange={handleChange}
                className="w-full rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 text-primary-900 placeholder-primary-400 focus:border-brand-500 focus:bg-white focus:ring-1 focus:ring-brand-500 focus:outline-none transition-all duration-200 text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-primary-700 mb-1.5">
                E-mail <span className="text-brand-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="din@email.dk"
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 text-primary-900 placeholder-primary-400 focus:border-brand-500 focus:bg-white focus:ring-1 focus:ring-brand-500 focus:outline-none transition-all duration-200 text-sm"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-primary-700 mb-1.5">
              Beskrivelse af opgaven <span className="text-brand-600">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              placeholder="Beskriv dit projekt, f.eks. renovering, ny træterrasse, udskiftning af vinduer..."
              value={formData.description}
              onChange={handleChange}
              className="w-full rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 text-primary-900 placeholder-primary-400 focus:border-brand-500 focus:bg-white focus:ring-1 focus:ring-brand-500 focus:outline-none transition-all duration-200 text-sm resize-none"
            />
          </div>

          {status === "error" && (
            <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 border border-rose-200 p-3.5 text-rose-800 text-xs">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 transition-colors duration-200 cursor-pointer"
          >
            {status === "loading" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
